import React, { useCallback, useEffect, useState } from 'react';
import { Card, Tag, Typography, Row, Col, message, Pagination, Tabs, TabsProps, Button } from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import {apiGetIrisProviders, apiGetListNetworkProvider, apiGetTDMProviders, apiGetVDSProviders} from '../../api';
import { formatCurrency } from '../../utils/string-utils';
import { useLoading } from '../../context/LoadingContext';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import {ACCOUNT_MANAGEMENT, CREATE_NETWORK_PROVIDER, CREATE_VDS_PROVIDER, CREATE_TDM_PROVIDER, CREATE_IRIS_PROVIDER, PROVIDER_LIST, EDIT_NETWORK_PROVIDER, EDIT_VDS_PROVIDER, EDIT_TDM_PROVIDER, EDIT_IRIS_PROVIDER} from '../../assets/constants/route_path';
import {cleanArray} from "../../utils/common-func";

const { Title, Text } = Typography;

interface NetworkProvider {
  id: string;
  name: string;
  cpCode?: string;
  source?: string;
  isActive: boolean;
  balance?: number | string;
  proxyAddress: string[];
}

export default function ProviderList() {
  // State for each provider type
  const [dataSponsorProviders, setDataSponsorProviders] = useState<NetworkProvider[]>([]);
  const [affapiProviders, setAffapiProviders] = useState<NetworkProvider[]>([]);
  const [vdsProviders, setVdsProviders] = useState<NetworkProvider[]>([]);
  const [irisProviders, setIrisProviders] = useState<NetworkProvider[]>([]);
  const [tkgProviders, setTkgProviders] = useState<NetworkProvider[]>([]);
  const [tv360Providers, setTv360Providers] = useState<NetworkProvider[]>([]);
  const [tdmProviders, setTdmProviders] = useState<NetworkProvider[]>([]);

  // Pagination state for each tab
  const [currentPageDataSponsor, setCurrentPageDataSponsor] = useState(1);
  const [pageSizeDataSponsor, setPageSizeDataSponsor] = useState(10);
  const [currentPageAffapi, setCurrentPageAffapi] = useState(1);
  const [pageSizeAffapi, setPageSizeAffapi] = useState(10);
  const [currentPageVds, setCurrentPageVds] = useState(1);
  const [pageSizeVds, setPageSizeVds] = useState(10);
  const [currentPageIris, setCurrentPageIris] = useState(1);
  const [pageSizeIris, setPageSizeIris] = useState(10);
  const [currentPageTkg, setCurrentPageTkg] = useState(1);
  const [pageSizeTkg, setPageSizeTkg] = useState(10);
  const [currentPageTv360, setCurrentPageTv360] = useState(1);
  const [pageSizeTv360, setPageSizeTv360] = useState(10);
  const [currentPageTdm, setCurrentPageTdm] = useState(1);
  const [pageSizeTdm, setPageSizeTdm] = useState(10);

  const { setIsLoading } = useLoading();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const previousTab = searchParams.get('previousTab');

  const fetchListNetworkProvider = useCallback(async () => {
    try {
      setIsLoading(true);
      const [resNetworkProviders, vdsProvidersRes, irisProviderRes, tdmProviderRes] = await Promise.all([
        apiGetListNetworkProvider(),
        apiGetVDSProviders(),
        apiGetIrisProviders(),
        apiGetTDMProviders(),
      ]);

      // Process VDS Providers
      if (vdsProvidersRes.data.vdsProviders != null) {
        setVdsProviders(vdsProvidersRes.data.vdsProviders);
      } else {
        setVdsProviders([]);
      }

      // Process IRIS Providers
      if (irisProviderRes.data.irisProviders != null) {
        setIrisProviders(irisProviderRes.data.irisProviders);
      } else {
        setIrisProviders([]);
      }

      // Process TDM Providers
      if (tdmProviderRes.data.promotionProviders != null) {
          setTdmProviders(tdmProviderRes.data.promotionProviders);
      } else {
          setTdmProviders([]);
      }

      // Process Network Providers and categorize by source
      if (resNetworkProviders.data.networks != null) {
        const networks = resNetworkProviders.data.networks;

        // Filter by cpCode or source to categorize providers
        const dataSponsor = networks.filter((p: any) =>
          p.source === 'DataSponsor'
        );
        const affapi = networks.filter((p: any) =>
          p.source === 'Affapi'
        );
        const tkg = networks.filter((p: any) =>
          p.source === 'TKG'
        );
        const tv360 = networks.filter((p: any) =>
          p.source === 'TV360Viettel'
        );

        setDataSponsorProviders(dataSponsor);
        setAffapiProviders(affapi);
        setTkgProviders(tkg);
        setTv360Providers(tv360);
      } else {
        setDataSponsorProviders([]);
        setAffapiProviders([]);
        setTkgProviders([]);
        setTv360Providers([]);
      }
    } catch (error) {
      console.error('Error fetching providers:', error);
      setDataSponsorProviders([]);
      setAffapiProviders([]);
      setVdsProviders([]);
      setIrisProviders([]);
      setTkgProviders([]);
      setTv360Providers([]);
      setTdmProviders([]);
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading]);

  useEffect(() => {
    fetchListNetworkProvider();
  }, [fetchListNetworkProvider]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    message.success('Đã sao chép vào clipboard');
  };

  const renderProviderCard = (provider: NetworkProvider, index: number, tabKey: string) => {
    const providerCode = provider.cpCode || provider.name || '-';
    const balance = Number(provider.balance || 0);
    const proxyAddresses = provider.proxyAddress ? cleanArray(provider.proxyAddress) : [];

    const handleEdit = () => {
      if (tabKey === 'vdsProviders') {
        navigate(ACCOUNT_MANAGEMENT + PROVIDER_LIST + EDIT_VDS_PROVIDER + `?name=${provider.name}&source=${tabKey}`, {
          state: { name: provider.name, source: tabKey }
        });
      } else if (tabKey === 'irisProviders') {
        navigate(ACCOUNT_MANAGEMENT + PROVIDER_LIST + EDIT_IRIS_PROVIDER + `?name=${provider.name}&source=${tabKey}`, {
          state: { name: provider.name, source: tabKey }
        });
      } else if (tabKey === 'TDMSource') {
        navigate(ACCOUNT_MANAGEMENT + PROVIDER_LIST + EDIT_TDM_PROVIDER + `?name=${provider.name}&source=${tabKey}`, {
          state: { name: provider.name, source: tabKey }
        });
      } else {
        navigate(ACCOUNT_MANAGEMENT + PROVIDER_LIST + EDIT_NETWORK_PROVIDER + `?cpCode=${provider.cpCode}&source=${tabKey}`, {
          state: { cpCode: provider.cpCode, source: tabKey }
        });
      }
    };

    return (
      <Card
        key={provider.id}
        style={{
          marginBottom: 12,
          border: '1px solid #f0f0f0',
          borderRadius: 8
        }}
      >
        <Row justify="space-between" align="top">
          <Col span={24}>
            <div style={{ marginBottom: 12 }}>
              <Row justify="space-between" align="middle">
                <Col>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      backgroundColor: '#1890ff',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: 16,
                    }}>
                      {index + 1}
                    </div>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Title level={4} style={{ margin: 0, marginBottom: 4 }}>
                          {provider.name}
                        </Title>
                      </div>
                      <Text type="secondary">
                        Mã nhà cung cấp: {providerCode} • ID: {provider.id}
                      </Text>
                    </div>
                  </div>
                </Col>
                {/*Not release in this version*/}
                {/*<Col>*/}
                {/*  <Button*/}
                {/*    style={{color: 'white', background: 'orange'}}*/}
                {/*    icon={<EditOutlined />}*/}
                {/*    onClick={handleEdit}*/}
                {/*  />*/}
                {/*</Col>*/}
              </Row>
            </div>

            <Row gutter={32}>
              <Col span={12}>
                <div style={{ marginBottom: 12 }}>
                  <Text strong>Số dư</Text>
                  <div style={{ marginTop: 4 }}>
                    <Text style={{ fontSize: 16, color: '#1890ff' }}>
                      {formatCurrency(Number.isNaN(balance) ? 0 : balance)}
                    </Text>
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 12 }}>
                  <Text strong>Trạng thái</Text>
                  <div style={{ marginTop: 4 }}>
                    <Tag color={provider.isActive ? 'green' : 'red'}>
                      {provider.isActive ? 'Hoạt động' : 'Không hoạt động'}
                    </Tag>
                  </div>
                </div>
              </Col>
            </Row>

            <div style={{ marginBottom: 12 }}>
              <Text strong>Proxy</Text>
              {proxyAddresses.length > 0 ? (
                <div style={{
                  marginTop: 8,
                  padding: '8px 12px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: 4,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 8
                }}>
                  {proxyAddresses.map((item: string, idx: number) => (
                    <Tag
                      key={idx}
                      color="blue"
                      style={{ cursor: 'pointer' }}
                      onClick={() => copyToClipboard(item)}
                    >
                      {item}
                    </Tag>
                  ))}
                </div>
              ) : (
                <div style={{
                  marginTop: 8,
                  padding: '8px 12px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: 4
                }}>
                  <Text type="secondary" style={{ fontSize: 14 }}>
                    Không có proxy
                  </Text>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Card>
    );
  };

  const renderProviderList = (providers: NetworkProvider[], currentPage: number, pageSize: number, setCurrentPage: (page: number) => void, setPageSize: (size: number) => void, tabKey: string) => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedProviders = providers.slice(startIndex, endIndex);

    // Determine create link based on tab key
    let createLink;
    if (tabKey === 'vdsProviders') {
      createLink = ACCOUNT_MANAGEMENT + PROVIDER_LIST + CREATE_VDS_PROVIDER;
    } else if (tabKey === 'irisProviders') {
      createLink = ACCOUNT_MANAGEMENT + PROVIDER_LIST + CREATE_IRIS_PROVIDER;
    } else if (tabKey === 'TDMSource') {
      createLink = ACCOUNT_MANAGEMENT + PROVIDER_LIST + CREATE_TDM_PROVIDER;
    } else {
      createLink = ACCOUNT_MANAGEMENT + PROVIDER_LIST + CREATE_NETWORK_PROVIDER;
    }

    return (
      <div>
        <Card style={{ marginBottom: 24 }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={3} style={{ margin: 0 }}>
                Nhà cung cấp
              </Title>
              <Text type="secondary">
                Quản lý nhà cung cấp và cấu hình
              </Text>
            </Col>
            <Col>
              <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: 5 }}>
                <div>
                  <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
                    <Text>Tổng số nhà cung cấp </Text>
                    {providers.length}
                  </Title>
                </div>
                {/*Not release in this version*/}
                {/*<Link to={createLink}>*/}
                {/*  <Button type="primary" icon={<PlusOutlined />}>*/}
                {/*    Thêm mới*/}
                {/*  </Button>*/}
                {/*</Link>*/}
              </div>
            </Col>
          </Row>
        </Card>

        {providers.length > 0 ? (
          <div>
            {paginatedProviders.map((provider, index) => renderProviderCard(provider, startIndex + index, tabKey))}
            <Card style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ color: '#595959', fontSize: 14 }}>
                  Hiển thị <strong>{startIndex + 1}-{Math.min(endIndex, providers.length)}</strong> / <strong>{providers.length}</strong> nhà cung cấp
                </div>
                <Pagination
                  current={currentPage}
                  pageSize={pageSize}
                  total={providers.length}
                  showSizeChanger
                  onChange={(page, size) => {
                    setCurrentPage(page);
                    if (size !== pageSize) {
                      setPageSize(size);
                      setCurrentPage(1);
                    }
                  }}
                />
              </div>
            </Card>
          </div>
        ) : (
          <Card>
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Title level={4} type="secondary">
                Không tìm thấy nhà cung cấp
              </Title>
              <Text type="secondary">
                Chưa có dữ liệu nhà cung cấp mạng
              </Text>
            </div>
          </Card>
        )}
      </div>
    );
  };

  const itemsTab: TabsProps['items'] = [
    {
      key: 'DataSponsor',
      label: `Đại trà`,
      children: renderProviderList(dataSponsorProviders, currentPageDataSponsor, pageSizeDataSponsor, setCurrentPageDataSponsor, setPageSizeDataSponsor, 'DataSponsor'),
    },
    {
      key: 'Affapi',
      label: `Upgrade`,
      children: renderProviderList(affapiProviders, currentPageAffapi, pageSizeAffapi, setCurrentPageAffapi, setPageSizeAffapi, 'Affapi'),
    },
    {
      key: 'vdsProviders',
      label: `VDS`,
      children: renderProviderList(vdsProviders, currentPageVds, pageSizeVds, setCurrentPageVds, setPageSizeVds, 'vdsProviders'),
    },
    {
      key: 'irisProviders',
      label: `IRIS`,
      children: renderProviderList(irisProviders, currentPageIris, pageSizeIris, setCurrentPageIris, setPageSizeIris, 'irisProviders'),
    },
    {
      key: 'TKG',
      label: `TKG`,
      children: renderProviderList(tkgProviders, currentPageTkg, pageSizeTkg, setCurrentPageTkg, setPageSizeTkg, 'TKG'),
    },
    {
      key: 'TV360Viettel',
      label: `TV360`,
      children: renderProviderList(tv360Providers, currentPageTv360, pageSizeTv360, setCurrentPageTv360, setPageSizeTv360, 'TV360Viettel'),
    },
    {
      key: 'TDMSource',
      label: `Thủ đô`,
      children: renderProviderList(tdmProviders, currentPageTdm, pageSizeTdm, setCurrentPageTdm, setPageSizeTdm, 'TDMSource'),
    },
  ];

  return (
    <div>
      <Tabs type='card' defaultActiveKey={previousTab || 'DataSponsor'} items={itemsTab} />
    </div>
  );
}

